import type { SceneManager } from '../scene/SceneManager';

/**
 * Panel flotante de ambiente (estilo configurador de referencia): posición del sol,
 * modo noche y dirección norte. Se abre/cierra desde el botón de iluminación.
 */
export class SceneControls {
  private readonly panel: HTMLElement;
  private open = false;

  constructor(
    private readonly sceneManager: SceneManager,
    private readonly toggleButton: HTMLButtonElement,
    parent: HTMLElement,
  ) {
    this.panel = this.build();
    parent.appendChild(this.panel);
    this.toggleButton.addEventListener('click', () => this.toggle());
  }

  toggle(): void {
    this.open = !this.open;
    this.panel.hidden = !this.open;
    this.toggleButton.setAttribute('aria-pressed', String(this.open));
  }

  private build(): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'scene-panel';
    panel.hidden = true;

    const header = document.createElement('div');
    header.className = 'sp-header';
    const title = document.createElement('span');
    title.textContent = 'Ambiente';
    const close = document.createElement('button');
    close.className = 'sp-close';
    close.type = 'button';
    close.textContent = '×';
    close.addEventListener('click', () => this.toggle());
    header.append(title, close);
    panel.append(header);

    // Posición del sol.
    panel.append(
      this.slider('Posición del sol', 'Mañana', 'Atardecer', 0, 1, 0.01, 0.62, (v) =>
        this.sceneManager.setSunPosition(v),
      ),
    );

    // Modo noche.
    const nightRow = document.createElement('div');
    nightRow.className = 'sp-row';
    const nightLabel = document.createElement('div');
    nightLabel.className = 'sp-label';
    nightLabel.textContent = 'Modo noche';
    const sw = document.createElement('button');
    sw.type = 'button';
    sw.className = 'sp-switch';
    sw.setAttribute('aria-pressed', 'false');
    sw.addEventListener('click', () => {
      const on = sw.getAttribute('aria-pressed') !== 'true';
      sw.setAttribute('aria-pressed', String(on));
      this.sceneManager.setNight(on);
    });
    const swText = document.createElement('span');
    swText.className = 'sp-switch-hint';
    swText.textContent = 'Apaga el sol para ver la iluminación LED';
    nightRow.append(nightLabel, sw, swText);
    panel.append(nightRow);

    // Dirección norte.
    panel.append(
      this.slider('Dirección norte', '0°', '360°', 0, 360, 1, 0, (v) =>
        this.sceneManager.setNorth(v),
      ),
    );

    return panel;
  }

  private slider(
    label: string,
    minLabel: string,
    maxLabel: string,
    min: number,
    max: number,
    step: number,
    value: number,
    onInput: (v: number) => void,
  ): HTMLElement {
    const row = document.createElement('div');
    row.className = 'sp-row';
    const l = document.createElement('div');
    l.className = 'sp-label';
    l.textContent = label;
    const input = document.createElement('input');
    input.type = 'range';
    input.className = 'sp-range';
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.value = String(value);
    input.addEventListener('input', () => onInput(Number(input.value)));
    const ends = document.createElement('div');
    ends.className = 'sp-ends';
    const a = document.createElement('span');
    a.textContent = minLabel;
    const b = document.createElement('span');
    b.textContent = maxLabel;
    ends.append(a, b);
    row.append(l, input, ends);
    return row;
  }
}
